import React, { useMemo } from "react";
import ReactSelect from "react-select";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@cmp-src/primitives/Select";
import { useIsMobile } from "@v2/hooks/use-mobile";
import { ArrowDownIcon } from "@v2/icons";

type Props = {
  officeId: string | null;
  setOfficeId: React.Dispatch<React.SetStateAction<string>>;
  offices: { id: string | null; name: string }[];
  className?: string;
  classNamePrefix?: string;
  readonly?: boolean;
  customIcon?: React.ReactNode;
};

export default function OfficeFilterDropdown({
  offices,
  setOfficeId,
  officeId,
  className = "",
  classNamePrefix = "react-select",
  readonly = false,
  customIcon
}: Props) {
  const isMobile = useIsMobile();

  const options = useMemo(() => {
    const officeValues = offices.map((x) => ({ value: x.id, label: x.name }));
    return officeValues;
  }, [offices]);
  const value = options.find((x) => x.value === officeId);

  if (isMobile) {
    return (
      <Select
        value={value?.value || undefined}
        onValueChange={(id) => {
          if (readonly) {
            return;
          }
          setOfficeId(id ?? "");
        }}
        disabled={readonly}
      >
        <SelectTrigger
          variant={"gray"}
          className={`mt-1 min-h-[42px] ${className}`}
        >
          <SelectValue className="truncate" placeholder="Select office" />
          {customIcon || <ArrowDownIcon className="h-4 w-4 opacity-50" />}
        </SelectTrigger>
        <SelectContent>
          {options.map((office) => (
            <SelectItem
              className={"text-nowrap w-full truncate"}
              key={office.value}
              value={office.value || ""}
            >
              {office.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const DropdownIndicator = (props: any) => {
    return (
      <div
        {...props.innerProps}
        className="flex items-center justify-center p-2"
      >
        {customIcon || <ArrowDownIcon className="h-4 w-4 opacity-50" />}
      </div>
    );
  };

  return (
    <ReactSelect
      menuPortalTarget={document.body}
      menuPosition={"fixed"}
      isDisabled={readonly}
      className={className}
      onChange={(d, event) => {
        if (readonly) {
          return;
        }
        setOfficeId(d?.value ?? "");
      }}
      value={value}
      options={options}
      classNamePrefix={classNamePrefix}
      components={{
        DropdownIndicator: customIcon ? DropdownIndicator : undefined
      }}
    />
  );
}
